import { useEffect, useRef } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { App as CapacitorApp } from "@capacitor/app";
import { analytics } from "@/services/analytics.service";
import { persistResumeQueryCache } from "@/lib/query/resumeQueryCache";
import { tokenManager } from "@/utils/token-manager";
import { isNativeApp } from "@/platform";

const SESSION_RESUME_THRESHOLD_MS = 30 * 60 * 1000;

interface NativeAppLifecycleOptions {
  isAuthenticated: boolean;
  queryClient: QueryClient;
  refreshResumeQueries: () => void;
  userId: string | null;
}

export function useNativeAppLifecycle({
  isAuthenticated,
  queryClient,
  refreshResumeQueries,
  userId,
}: NativeAppLifecycleOptions) {
  const lastBackgroundedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isNativeApp()) return;

    const listener = CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive) {
        document.querySelectorAll("video").forEach(video => {
          video.pause();
        });
        if (userId) {
          persistResumeQueryCache(queryClient, userId);
        }
        lastBackgroundedAtRef.current = Date.now();
        return;
      }

      const backgroundedAt = lastBackgroundedAtRef.current;
      if (isAuthenticated && backgroundedAt !== null) {
        const gapMs = Date.now() - backgroundedAt;
        if (gapMs >= SESSION_RESUME_THRESHOLD_MS) {
          analytics.capture("session_started", {
            trigger: "resumed",
            background_ms: gapMs,
          });
        }
      }
      lastBackgroundedAtRef.current = null;

      if (isAuthenticated && tokenManager.isTokenExpired()) {
        try {
          console.debug("[App] Refreshing expired token on app resume");
          await tokenManager.refreshAccessToken();
        } catch {
          console.debug("[App] Background token refresh failed - will retry on next API call");
        }
      }

      refreshResumeQueries();
    });

    return () => {
      void listener.then(subscription => subscription.remove());
    };
  }, [isAuthenticated, queryClient, refreshResumeQueries, userId]);
}
